import { z } from 'zod';

/**
 * Identifier conventions (CLAUDE.md §Core conventions, DESIGN_SPEC §7.1).
 *
 * Every id is a stable, opaque, prefixed string, unique within a tenant. The
 * prefix is load-bearing: `validateFinding` resolves a grounded id to an object
 * kind by its prefix, so a prefix that is ambiguous with another prefix is a
 * correctness bug, not a style question. `ids.test.ts` asserts the registry
 * stays unambiguous as prefixes are added.
 */
export const ID_PREFIXES = {
  // Platform
  tenant: 'tenant',
  /** A tenant's wrapped DEK in `tenant_keys` (docs/DECISIONS.md ZD-008). */
  tenant_key: 'key',
  user: 'user',
  project: 'project',
  assessment: 'assessment',

  // Input
  artefact: 'artefact',
  answer: 'answer',
  /** An artefact parsed into citable units (docs/DECISIONS.md ZD-015). */
  document_index: 'docindex',
  /** One ingestion run's ExtractedArchitecture (docs/DECISIONS.md ZD-014). */
  extraction: 'extraction',
  /** One persisted `CanonicalModel` (docs/DECISIONS.md ZD-028). */
  canonical_model: 'model',

  // Canonical architecture objects
  component: 'component',
  actor: 'actor',
  asset: 'asset',
  data_flow: 'flow',
  control_flow: 'ctrlflow',
  trust_boundary: 'boundary',
  environment: 'environment',
  existing_control: 'control',
  assumption: 'assumption',
  unknown: 'unknown',
  // Reserved — OT pack (docs/DECISIONS.md ZD-010).
  safety_function: 'safety_function',
  conflict: 'conflict',
  /**
   * Ties together the conflicts produced by one documentation disagreement
   * (docs/DECISIONS.md ZD-022).
   *
   * `cgroup` rather than the obvious `conflict_group`, because
   * `ConflictIdSchema` would accept `conflict_group_x` — the suffix charset
   * permits underscores, so it matches `^conflict_…` too. The registry test
   * below refuses a prefix another prefix can swallow; this is the first new id
   * kind since that test was written, and it caught this.
   */
  conflict_group: 'cgroup',

  // Knowledge
  pack: 'pack',
  threat_pattern: 'threat_pattern',
  /**
   * A control in the QRS catalogue (docs/DECISIONS.md ZD-032).
   *
   * Distinct from `control_*`, which is a control *observed in a client's
   * architecture*. This one is product knowledge: the mitigation a threat
   * pattern points at, at engineering granularity ("terminate TLS at the trust
   * boundary"), which is finer than any published framework operates at — which
   * is why the catalogue has to exist rather than patterns citing ISO or NIST
   * identifiers directly.
   *
   * Not opaque, and not tenant-scoped, for ZD-004's reason: like
   * `threat_pattern_*`, a dotted taxonomy is what keeps a catalogue of sixty
   * navigable, and there is no client data in it to leak.
   */
  qrs_control: 'qrsctl',
  /**
   * A standing, client-facing QRS artefact (docs/DECISIONS.md ZD-035) — content
   * an author wrote once, carried with its own identity rather than assembled
   * at render time from whatever data happens to be nearby.
   *
   * `qrsart_ce.scope_clarification` is the first: what Cyber Essentials
   * deliberately does not assess. It is a scope clarification of a
   * certification that does its own job well, which is a framing that has to
   * travel *with* the content — reconstructing it from unmapped mapping cells
   * at render time would lose the framing and leave a list of gaps reading as
   * criticism.
   *
   * Product knowledge, not tenant data, so it is readable rather than opaque
   * for ZD-004's reason. The catalogue suggested `qrsart.ce_scope_clarification`;
   * the prefix separator here is `_` and the dotted taxonomy goes in the suffix,
   * exactly as `qrsctl_access.authenticate_all_flows` does.
   */
  qrs_artefact: 'qrsart',
  plan: 'plan',

  // Process
  finding: 'finding',
  mapping: 'mapping',
  recommendation: 'rec',
  action: 'action',
  evidence: 'evidence',
  report: 'report',
  audit: 'audit',
  run: 'run',
  /**
   * One model call (docs/DECISIONS.md ZD-046). Distinct from `run_*`: a worker
   * run may make several calls, and cost is attributed per call.
   *
   * `llmcall` rather than `llm_call`, for the reason `cgroup` is not
   * `conflict_group` — the suffix charset permits underscores, so a prefix that
   * another prefix can swallow is a correctness bug rather than a style
   * question. Nothing swallows this one, and the registry test below is what
   * says so.
   */
  llm_call: 'llmcall',
  review: 'review',
  /**
   * One approver decision at the human gate (docs/DECISIONS.md ZD-061).
   *
   * **Not `review_*`, although that kind exists and has no producer.** In this
   * codebase "review" already means the *automated QA review* — DESIGN_SPEC §6.5
   * and `ReviewDecisionSchema`'s `approved | changes_required | rejected` are
   * that worker's output, and §11.1 exists to keep it distinct from the human
   * gate it may never substitute for. One name over both would be the two-things
   * -one-representation shape `CLAUDE.md` warns about, in the one place the
   * distinction is the product's central claim.
   *
   * Minted rather than natural, unlike `llm_cache`'s key: a changed mind is a
   * new record superseding an earlier one, so `(assessment, finding_key)` is not
   * unique across the history and `supersedes` needs something to point at.
   */
  decision: 'decision',
  /**
   * A group of decisions a reviewer recorded as one act (docs/DECISIONS.md
   * ZD-127). **`batch`, not `decision_batch`**: the suffix charset admits an
   * underscore, so `decision_` would swallow `decision_batch_*` and the
   * registry test below refuses it — the same reason `cgroup` is not
   * `conflict_group`. ZD-126 §7 proposed the longer name; it is the entry
   * proposing a name its own naming rule refuses, caught here rather than
   * five minutes into a build.
   */
  decision_batch: 'batch',
} as const;

export type IdKind = keyof typeof ID_PREFIXES;
export type IdPrefix = (typeof ID_PREFIXES)[IdKind];

export const ID_KINDS = Object.keys(ID_PREFIXES) as IdKind[];

/**
 * Suffix charset. Dots are permitted so pack authors can keep a navigable
 * taxonomy inside the id — `threat_pattern_it.transit.unencrypted_personal_data`
 * (docs/DECISIONS.md ZD-004). Nothing in this codebase parses the suffix for
 * meaning; a pattern's domain comes from its pack, never from its id.
 */
const ID_SUFFIX = '[A-Za-z0-9][A-Za-z0-9._-]*';

const PATTERNS = new Map<IdKind, RegExp>(
  ID_KINDS.map((kind) => [kind, new RegExp(`^${ID_PREFIXES[kind]}_${ID_SUFFIX}$`)]),
);

export function idPattern(kind: IdKind): RegExp {
  const pattern = PATTERNS.get(kind);
  /* c8 ignore next 3 -- unreachable: PATTERNS is built from ID_KINDS */
  if (!pattern) {
    throw new Error(`Unknown id kind: ${kind}`);
  }
  return pattern;
}

export function idSchema(kind: IdKind): z.ZodString {
  return z.string().regex(idPattern(kind), `must be a ${ID_PREFIXES[kind]}_* identifier`);
}

export function isId(kind: IdKind, value: string): boolean {
  return idPattern(kind).test(value);
}

/**
 * Resolves an id to its kind by longest matching prefix. Longest-match rather
 * than first-match so that adding a prefix which extends an existing one cannot
 * silently reclassify existing ids.
 */
export function idKindOf(value: string): IdKind | null {
  let match: IdKind | null = null;
  let matchLength = -1;
  for (const kind of ID_KINDS) {
    const prefix = ID_PREFIXES[kind];
    if (isId(kind, value) && prefix.length > matchLength) {
      match = kind;
      matchLength = prefix.length;
    }
  }
  return match;
}

/** Crockford-style base32: no i, l, o or u, so ids survive being read aloud. */
const ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
const ID_SUFFIX_LENGTH = 22;

interface RandomSource {
  getRandomValues<T extends ArrayBufferView>(array: T): T;
}

/** Structural, for the same reason `RandomSource` is: no runtime-specific lib types. */
interface DigestSource {
  digest(algorithm: string, data: ArrayBufferView): Promise<ArrayBuffer>;
}

/**
 * Mints a new opaque id. Reads Web Crypto off the global rather than importing
 * it, so this package stays runtime-agnostic: the same generator runs in the
 * Workers runtime and in Node.
 */
export function newId(kind: IdKind): string {
  const source = (globalThis as { crypto?: RandomSource }).crypto;
  if (!source?.getRandomValues) {
    throw new Error('Web Crypto is unavailable: cannot mint identifiers safely');
  }
  const bytes = source.getRandomValues(new Uint8Array(ID_SUFFIX_LENGTH));
  let suffix = '';
  for (const byte of bytes) {
    suffix += ALPHABET[byte % ALPHABET.length];
  }
  return `${ID_PREFIXES[kind]}_${suffix}`;
}

/** Injectable id source, so tests and replays can be deterministic. */
export type IdFactory = (kind: IdKind) => string;

/**
 * An id that is a **digest of what the object is**, not a fresh random value
 * (docs/DECISIONS.md ZD-028).
 *
 * `newId` is right for a thing that is new every time — a tenant, an artefact,
 * a run. A canonical architecture object is not: it is one identity expressed
 * in one assessment, and it has to come back with the same id on the next run,
 * for two reasons.
 *
 *  - `reproducibilityKey()` counts `grounded_object_ids`. Random ids would make
 *    CLAUDE.md principle 6's ≥90% target unreachable by construction, so the
 *    gate would be measuring the id generator rather than the pipeline.
 *  - Normalisation runs again after a clarification round. Random ids would
 *    leave every finding, conflict and answer pointing at objects that no
 *    longer exist.
 *
 * Opaque, because a digest is: `component_manifest-exchange` would be stable
 * and would leak a client's architecture into every log line and URL that
 * carries an id.
 *
 * Parts are joined with a NUL, which cannot occur in any of them, so
 * `('ab', 'c')` and `('a', 'bc')` cannot collide.
 */
export async function derivedId(kind: IdKind, ...parts: readonly string[]): Promise<string> {
  const subtle = (globalThis as { crypto?: { subtle?: DigestSource } }).crypto?.subtle;
  if (!subtle) {
    throw new Error('Web Crypto is unavailable: cannot derive identifiers');
  }
  // The view is handed to `digest` directly rather than its backing buffer:
  // reaching for `.buffer` would hand over bytes outside the view whenever the
  // encoder returns an offset one, which is the trap `toArrayBuffer` exists to
  // avoid in the R2 wrapper — and there, unlike here, there is no API taking a
  // view to sidestep it with.
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(parts.join('\u0000')));
  return `${ID_PREFIXES[kind]}_${base32(new Uint8Array(digest), ID_SUFFIX_LENGTH)}`;
}

/**
 * Five bits at a time out of the digest, rather than `byte % 32` — modulo over
 * 256 is biased towards the first 24 symbols, which is tolerable for random
 * bytes and wrong for a digest we want to spread evenly.
 */
function base32(bytes: Uint8Array, length: number): string {
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5 && out.length < length) {
      bits -= 5;
      out += ALPHABET[(buffer >> bits) & 31];
    }
    if (out.length >= length) break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Named id schemas
// ---------------------------------------------------------------------------

export const TenantIdSchema = idSchema('tenant');
export const TenantKeyIdSchema = idSchema('tenant_key');
export const UserIdSchema = idSchema('user');
export const ProjectIdSchema = idSchema('project');
export const AssessmentIdSchema = idSchema('assessment');
export const ArtefactIdSchema = idSchema('artefact');
export const DocumentIndexIdSchema = idSchema('document_index');
export const ExtractionIdSchema = idSchema('extraction');
export const CanonicalModelIdSchema = idSchema('canonical_model');
export const AnswerIdSchema = idSchema('answer');
export const ComponentIdSchema = idSchema('component');
export const ActorIdSchema = idSchema('actor');
export const AssetIdSchema = idSchema('asset');
export const DataFlowIdSchema = idSchema('data_flow');
export const ControlFlowIdSchema = idSchema('control_flow');
export const TrustBoundaryIdSchema = idSchema('trust_boundary');
export const EnvironmentIdSchema = idSchema('environment');
export const ExistingControlIdSchema = idSchema('existing_control');
export const AssumptionIdSchema = idSchema('assumption');
export const UnknownIdSchema = idSchema('unknown');
export const SafetyFunctionIdSchema = idSchema('safety_function');
export const ConflictIdSchema = idSchema('conflict');
export const ConflictGroupIdSchema = idSchema('conflict_group');
export const PackIdSchema = idSchema('pack');
export const ThreatPatternIdSchema = idSchema('threat_pattern');
export const QrsControlIdSchema = idSchema('qrs_control');
export const QrsArtefactIdSchema = idSchema('qrs_artefact');
export const PlanIdSchema = idSchema('plan');
export const FindingIdSchema = idSchema('finding');
export const MappingIdSchema = idSchema('mapping');
export const RecommendationIdSchema = idSchema('recommendation');
export const ActionIdSchema = idSchema('action');
export const EvidenceIdSchema = idSchema('evidence');
export const ReportIdSchema = idSchema('report');
export const AuditIdSchema = idSchema('audit');
export const RunIdSchema = idSchema('run');
export const LlmCallIdSchema = idSchema('llm_call');
export const ReviewIdSchema = idSchema('review');
export const DecisionIdSchema = idSchema('decision');
export const DecisionBatchIdSchema = idSchema('decision_batch');

/** Anything a worker may cite as evidence: an uploaded artefact or an answer. */
export const EvidenceRefSchema = z.union([ArtefactIdSchema, AnswerIdSchema]);
export type EvidenceRef = z.infer<typeof EvidenceRefSchema>;

/*
 * `Locator` and `EvidenceCitation` used to live here. They moved to
 * `./locator` when the region gained structure and locators gained a matching
 * rule (docs/DECISIONS.md ZD-024) — this module answers "what is a well-formed
 * identifier", and that one answers "what is a well-formed location". They are
 * re-exported from the package index, so importers are unaffected.
 */

/** `pack_qrs-it@0.1.0` — a pack pinned to an exact version (DESIGN_SPEC §12.2). */
export const PackBindingSchema = z
  .string()
  .regex(
    /^pack_[A-Za-z0-9][A-Za-z0-9._-]*@\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/,
    'must be pack_<id>@<semver>',
  );
export type PackBinding = z.infer<typeof PackBindingSchema>;

export function parsePackBinding(binding: string): { pack_id: string; pack_version: string } {
  const at = binding.lastIndexOf('@');
  return {
    pack_id: binding.slice(0, at),
    pack_version: binding.slice(at + 1),
  };
}
